'use client'

// ── The AI identity, as a primitive ─────────────────────────────────────────
//
// One home for the "this is AI — click me" affordance: the brand aurora
// (blue → purple → green) as a slowly sweeping ring that quickens on hover.
// Born in the ⌘K search modal (7/19); promoted here when Ask Jarvis needed
// the same identity on every panel. The modal imports AI_GRADIENT from here
// now — one gradient, one sweep, everywhere.
//
// House rules honored: inline styles + color-mix (no new tailwind boxShadow
// family, so no SOLIS_SHADOW_TOKENS registration), keyframes live in
// app/globals.css with prefers-reduced-motion twins, and every *moving* part
// also checks useReducedMotion so the ring renders statically rather than
// vanishing.

import { useState, type ReactNode } from 'react'
import { Sparkles } from 'lucide-react'
import { useReducedMotion } from 'framer-motion'

export const AI_GRADIENT =
  'linear-gradient(100deg, var(--sb-blue-500), var(--sb-purple-600), var(--sb-green-500), var(--sb-blue-500))'

const SHEEN_REST_MS = 5200
const SHEEN_HOVER_MS = 1800

/**
 * The gradient ring, as a wrapper component. The ring itself is a STATIC
 * gradient (zero per-frame work at rest); the "alive" tell is a sheen
 * overlay sweeping by on transform + opacity only, which the compositor
 * animates without repainting — an earlier cut animated the gradient's
 * background-position, which repaints every frame, and these rings sit on
 * always-visible chrome (Anh's perf pass, 7/31). Used by <AiGlowButton> and
 * the sidebar search trigger.
 */
export function AiGlowRing({
  children,
  reducedMotion,
  hovered = false,
  intensity = 1,
  radius = 999,
  ringWidth = 1.5,
}: {
  children: ReactNode
  reducedMotion: boolean | null
  hovered?: boolean
  /** 0..1 — how loud the ring is at rest. */
  intensity?: number
  radius?: number
  ringWidth?: number
}) {
  return (
    <span
      style={{
        position: 'relative',
        display: 'inline-flex',
        overflow: 'hidden',
        isolation: 'isolate',
        padding: ringWidth,
        borderRadius: radius,
        backgroundImage: AI_GRADIENT,
        opacity: hovered ? 1 : 0.4 + 0.6 * intensity,
        transition: 'opacity 150ms ease, box-shadow 150ms ease',
        boxShadow: hovered
          ? '0 2px 10px color-mix(in srgb, var(--sb-purple-500) 28%, transparent)'
          : '0 1px 6px color-mix(in srgb, var(--sb-blue-500) 14%, transparent)',
      }}
    >
      {!reducedMotion && (
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: -1,
            background:
              'linear-gradient(105deg, transparent 30%, color-mix(in srgb, white 65%, transparent) 50%, transparent 70%)',
            animation: `sb-ai-sheen ${hovered ? SHEEN_HOVER_MS : SHEEN_REST_MS}ms ease-in-out infinite`,
            willChange: 'transform, opacity',
          }}
        />
      )}
      {children}
    </span>
  )
}

/**
 * A compact pill button wearing the AI identity: gradient ring with a slow
 * rest-state sweep (the "alive" tell that says click me), quickening on
 * hover, sparkle accent pulsing alongside. Label stays on the app's normal
 * text ramp — the ring is the identity, the words stay legible.
 */
export function AiGlowButton({
  children,
  badge,
  onClick,
  disabled,
  title,
  'aria-label': ariaLabel,
}: {
  children: ReactNode
  /** Quiet count inside the button (thread size, NOT a notification — no
   *  red, no pulse; Anh's ruling 7/31 replacing the separate count chip). */
  badge?: number
  onClick?: () => void
  disabled?: boolean
  title?: string
  'aria-label'?: string
}) {
  const [hovered, setHovered] = useState(false)
  const reducedMotion = useReducedMotion()

  return (
    <AiGlowRing reducedMotion={reducedMotion || disabled ? true : null} hovered={hovered} intensity={disabled ? 0 : 0.55}>
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        title={title}
        aria-label={ariaLabel}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setHovered(true)}
        onBlur={() => setHovered(false)}
        className="text-b3 font-medium"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          padding: '3px 10px',
          borderRadius: 999,
          border: 'none',
          background: hovered
            ? 'linear-gradient(135deg, var(--sb-blue-50), var(--sb-purple-50))'
            : 'var(--sb-color-surface-default)',
          color: hovered ? 'var(--sb-purple-800)' : 'var(--sb-color-ai-foreground)',
          cursor: disabled ? 'default' : 'pointer',
          opacity: disabled ? 0.6 : 1,
          transition: 'background 150ms ease, color 150ms ease',
          whiteSpace: 'nowrap',
        }}
      >
        <Sparkles
          size={12}
          aria-hidden="true"
          style={{
            color: 'var(--sb-purple-600)',
            animation: reducedMotion || disabled ? undefined : 'sb-ai-sparkle-pulse 2.2s ease-in-out infinite',
          }}
        />
        {children}
        {typeof badge === 'number' && badge > 0 && (
          <span
            className="text-panel-label font-medium"
            style={{
              padding: '1px 6px',
              borderRadius: 999,
              background: hovered
                ? 'color-mix(in srgb, var(--sb-purple-100) 70%, white)'
                : 'var(--sb-color-surface-subtle)',
              color: 'var(--sb-color-ai-foreground)',
              transition: 'background 150ms ease',
            }}
          >
            {badge}
          </span>
        )}
      </button>
    </AiGlowRing>
  )
}
